import { useEffect, useState } from 'react';
import {
  moveBlock,
  parseStoredBlockLayout,
  reconcileBlockLayout,
  toggleBlockVisible,
  visibleBlockIds,
  type StoredBlockLayout,
} from './block-layout';

export interface BlockLayoutDef {
  id: string;
  label: string;
}

export interface BlockLayout {
  /** Alle Block-IDs in konfigurierter Reihenfolge (inkl. ausgeblendeter). */
  order: string[];
  /** Nur sichtbare IDs, in Reihenfolge — direkt zum Rendern iterierbar. */
  visibleIds: string[];
  isHidden(id: string): boolean;
  toggleVisible(id: string): void;
  moveUp(id: string): void;
  moveDown(id: string): void;
  reset(): void;
}

const STORAGE_PREFIX = 'fakt:block-layout:';

function readStorage(storageKey: string): StoredBlockLayout | null {
  if (typeof window === 'undefined') return null;
  try {
    return parseStoredBlockLayout(window.localStorage.getItem(STORAGE_PREFIX + storageKey));
  } catch {
    return null;
  }
}

function writeStorage(storageKey: string, layout: StoredBlockLayout): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(layout));
  } catch {
    // Storage nicht verfügbar (Privacy-Mode, Quota voll) — silently ignore,
    // Layout bleibt für die Session im State erhalten (vgl. usePersistedToggle).
  }
}

/**
 * Ein-/Ausblenden + Reihenfolge bestehender Seiten-Blöcke, localStorage-
 * persistiert (BL-219) — analog `invoice-drafts.ts`/`usePersistedToggle`.
 *
 * Bewusst simpel gehalten (Projektlead-Entscheidung nach zwei
 * PM-Sparring-Runden gegen die volle Widget-Architektur, siehe
 * docs/BL-219-widget-dashboard-architektur.md): kein Drag&Drop, keine neue
 * Library, kein Widget-Registry — jeder Block bleibt exakt die bestehende
 * Komponente, Auf/Ab-Buttons statt Drag, ein JSON-Objekt pro Seite im
 * localStorage.
 *
 * `blocks` sollte eine auf Modulebene definierte, stabile Konstante sein —
 * ihre Reihenfolge ist die Default-Reihenfolge beim allerersten Aufruf.
 */
export function useBlockLayout(storageKey: string, blocks: BlockLayoutDef[]): BlockLayout {
  const defaultOrder = blocks.map((b) => b.id);
  const [layout, setLayout] = useState<StoredBlockLayout>(() =>
    reconcileBlockLayout(readStorage(storageKey), defaultOrder),
  );

  useEffect(() => {
    writeStorage(storageKey, layout);
  }, [storageKey, layout]);

  return {
    order: layout.order,
    visibleIds: visibleBlockIds(layout),
    isHidden: (id) => layout.hidden.includes(id),
    toggleVisible: (id) => setLayout((prev) => toggleBlockVisible(prev, id)),
    moveUp: (id) => setLayout((prev) => moveBlock(prev, id, -1)),
    moveDown: (id) => setLayout((prev) => moveBlock(prev, id, 1)),
    reset: () => setLayout({ order: defaultOrder, hidden: [] }),
  };
}
